// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import { useState, useEffect } from "react";

import { generateClient } from "@aws-amplify/api";

import {
	UsageRecord,
	UsageSummary,
	UserUsageBreakdown,
} from "../../../types/usageCostAllocation";
import { usageListAllRecords } from "../../../graphql/usageCostAllocation/queries";
import {
	computeUsageSummary,
	sortRecordsByTimestampDesc,
} from "../../usage/hooks/useUsageRecords";

const client = generateClient({ authMode: "userPool" });

export interface UseAdminUsageRecordsResult {
	records: UsageRecord[];
	userBreakdown: UserUsageBreakdown[];
	summary: UsageSummary;
	loading: boolean;
	error: string | null;
}

export function filterByJobType(
	records: UsageRecord[],
	jobType?: string
): UsageRecord[] {
	if (!jobType) {
		return records;
	}
	return records.filter((r) => r.jobType === jobType);
}

export function computeUserBreakdown(
	records: UsageRecord[]
): UserUsageBreakdown[] {
	const map = new Map<string, UserUsageBreakdown>();

	for (const record of records) {
		let entry = map.get(record.userId);
		if (!entry) {
			entry = {
				userId: record.userId,
				userName: record.userName,
				totalCharacterCount: 0,
				totalEstimatedCost: 0,
			};
			map.set(record.userId, entry);
		}

		// Update userName if a newer record has it
		if (record.userName && !entry.userName) {
			entry.userName = record.userName;
		}

		entry.totalCharacterCount += record.metricValue;
		entry.totalEstimatedCost += record.estimatedCost;
	}

	return Array.from(map.values());
}

export function useAdminUsageRecords(
	billingPeriod: string,
	jobTypeFilter?: string
): UseAdminUsageRecordsResult {
	const [records, setRecords] = useState<UsageRecord[]>([]);
	const [userBreakdown, setUserBreakdown] = useState<UserUsageBreakdown[]>([]);
	const [summary, setSummary] = useState<UsageSummary>({
		totalCharacterCount: 0,
		totalEstimatedCost: 0,
	});
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		async function fetchRecords() {
			setLoading(true);
			setError(null);
			try {
				const variables: { billingPeriod: string; jobType?: string } = {
					billingPeriod,
				};
				if (jobTypeFilter) {
					variables.jobType = jobTypeFilter;
				}

				const response = await client.graphql({
					query: usageListAllRecords,
					variables,
				});
				const result = (response as any).data.usageListAllRecords;
				const data: UsageRecord[] =
					(Array.isArray(result) ? result : result?.items) ?? [];
				const sorted = sortRecordsByTimestampDesc(data);
				setRecords(sorted);
				setSummary(computeUsageSummary(sorted));
				setUserBreakdown(computeUserBreakdown(sorted));
			} catch (err: any) {
				console.error("Failed to fetch admin usage records", err);
				setError(err?.message ?? "Failed to fetch admin usage records");
			} finally {
				setLoading(false);
			}
		}
		fetchRecords();
	}, [billingPeriod, jobTypeFilter]);

	return { records, userBreakdown, summary, loading, error };
}
